import React, { useState } from 'react';
import { CheckCircle, FileText, Download, UserPlus, CreditCard, AlertCircle, Info } from 'lucide-react';

const Admissions = () => {
    const [faqOpen, setFaqOpen] = useState(null);

    const toggleFaq = (i) => setFaqOpen(faqOpen === i ? null : i);

    return (
        <div className="font-sans text-gray-800 bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-primary text-white py-32 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-scales.png')] opacity-20"></div>
                <div className="container relative z-10">
                    <span className="text-secondary font-bold tracking-widest uppercase mb-4 block animate-fade-in-up">Join Our Family</span>
                    <h1 className="text-5xl md:text-7xl font-black mb-6">Admissions</h1>
                    <p className="max-w-2xl mx-auto text-blue-100 text-xl font-light">
                        We welcome students who are curious, driven, and ready to make a difference. Applications for 2026-27 are now open.
                    </p>
                </div>
            </div>

            {/* Intro Text */}
            <div className="container -mt-10 relative z-20 mb-20">
                <div className="bg-white p-12 rounded-3xl shadow-xl border border-gray-100 text-center max-w-5xl mx-auto">
                    <h2 className="text-3xl font-black text-primary mb-6">Why Early Application Matters?</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-8">
                        TCM Matriculation School maintains a strict student-teacher ratio to ensure personalized attention. As a result, seats in Kindergarten and Grade 1 are highly competitive and fill up fast. We encourage parents to start the inquiry process as early as January.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2"><CheckCircle size={16} /> Merit Scholarships Available</span>
                        <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2"><CheckCircle size={16} /> Sibling Discounts</span>
                        <span className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2"><CheckCircle size={16} /> Transport Facility</span>
                    </div>
                </div>
            </div>

            {/* The Process - Timeline Style */}
            <div className="container py-12">
                <h2 className="text-4xl font-black text-primary text-center mb-16">The Admission Procedure</h2>
                <div className="grid md:grid-cols-4 gap-8">
                    {[
                        { step: "01", icon: FileText, title: "Inquiry & Registration", desc: "Visit the school office or fill the online form. Purchase the application kit for ₹500." },
                        { step: "02", icon: UserPlus, title: "Document Submission", desc: "Submit filled form with birth certificate, photos, and previous academic records." },
                        { step: "03", icon: AlertCircle, title: "Interaction / Test", desc: "KG: Interaction with parents/child. Grade 1+: Basic aptitude test in Eng/Math." },
                        { step: "04", icon: CreditCard, title: "Fee Payment", desc: "Once selected, pay the admission fee within 7 days to confirm the seat." }
                    ].map((item, i) => (
                        <div key={i} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all border-t-4 border-secondary relative group">
                            <div className="absolute top-4 right-4 text-6xl font-black text-gray-100 -z-0 group-hover:text-gray-200 transition-colors">{item.step}</div>
                            <div className="relative z-10">
                                <div className="w-14 h-14 bg-primary text-white rounded-xl flex items-center justify-center mb-6">
                                    <item.icon size={28} />
                                </div>
                                <h3 className="text-xl font-bold text-primary mb-3">{item.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Age Criteria Table */}
            <div className="py-24 bg-white border-y border-gray-200">
                <div className="container">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <h2 className="text-3xl font-black text-primary mb-6">Age Criteria</h2>
                            <p className="text-gray-500 mb-8">For the Academic Year commencing June 2026. Note that age is calculated as of 31st May.</p>
                            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                                <table className="w-full text-left">
                                    <thead className="bg-primary text-white">
                                        <tr>
                                            <th className="p-4 font-bold">Class</th>
                                            <th className="p-4 font-bold">Minimum Age</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {[
                                            { c: "Pre-KG", a: "2 Years 6 Months" },
                                            { c: "LKG", a: "3 Years 6 Months" },
                                            { c: "UKG", a: "4 Years 6 Months" },
                                            { c: "Grade 1", a: "5 Years 6 Months" },
                                            { c: "Grade 2", a: "6 Years 6 Months" }
                                        ].map((row, i) => (
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="p-4 font-bold text-gray-700">{row.c}</td>
                                                <td className="p-4 text-gray-600">{row.a}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div>
                            <h2 className="text-3xl font-black text-primary mb-6">Documents Checklist</h2>
                            <p className="text-gray-500 mb-8">Please bring original documents for verification along with one set of photocopies.</p>
                            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 space-y-4">
                                {[
                                    "Birth Certificate (Original + Copy)",
                                    "Transfer Certificate (Original - for Grade 2 and up)",
                                    "Community Certificate (Copy)",
                                    "Aadhar Card of Student and Parents (Copy)",
                                    "Recent Passport size photos (5 Nos)",
                                    "Medical Fitness Certificate / Vaccination Record",
                                    "Report Card from previous school"
                                ].map((doc, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <CheckCircle size={20} className="text-green-500 shrink-0 mt-0.5" />
                                        <span className="text-gray-700 font-medium">{doc}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fee Structure (Indicative) */}
            <div className="py-24 container max-w-4xl">
                <div className="bg-secondary/10 p-8 rounded-2xl border border-secondary/20">
                    <h3 className="text-2xl font-black text-primary mb-4 flex items-center gap-3">
                        <Info size={28} className="text-secondary" /> Fee Policy Overview
                    </h3>
                    <p className="text-gray-700 mb-4">
                        We maintain a transparent fee structure with no hidden costs. Fees are payable in <strong>three terms</strong> (June, October, January).
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
                        <li>Tuition Fee includes all academic services, library, and lab usage.</li>
                        <li>Books, Uniforms, and Transport fees are separate.</li>
                        <li>A one-time admission fee is applicable for new students.</li>
                    </ul>
                    <p className="text-sm text-gray-500">
                        * For detailed fee structure grade-wise, please contact the accounts office directly.
                    </p>
                </div>
            </div>

            {/* FAQs */}
            <div className="container max-w-3xl">
                <h2 className="text-4xl font-black text-primary text-center mb-12">Admissions FAQ</h2>
                <div className="space-y-4">
                    {[
                        { q: "Do you provide sibling concessions?", a: "Yes, a concession of 10% on the Tuition Fee is offered to the younger sibling as long as both study at TCM." },
                        { q: "Is admission to Grade 10 and 12 direct?", a: "Direct admission to Board Exam classes is generally not encouraged but may be considered for transfer cases with a strong academic record, subject to CEO approval." },
                        { q: "What is the timing of the school?", a: "Kindergarten: 9:00 AM to 12:30 PM. Grade 1-12: 8:50 AM to 4:40 PM. Office hours are 8:30 AM to 5:30 PM." },
                        { q: "Is transport mandatory?", a: "No, transport is optional. However, for safety reasons, we discourage private vans. Parents can drop/pick up or use school buses." }
                    ].map((item, i) => (
                        <div key={i} className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
                            <button onClick={() => toggleFaq(i)} className="w-full text-left p-6 font-bold text-gray-800 flex justify-between items-center hover:bg-gray-50">
                                {item.q}
                                <span className={`transform transition-transform ${faqOpen === i ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                            {faqOpen === i && (
                                <div className="p-6 pt-0 text-gray-600 leading-relaxed border-t border-gray-100 bg-gray-50">
                                    {item.a}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Application Download CTA */}
            <div className="py-24 text-center mt-12">
                <button className="bg-primary text-white text-xl font-bold py-5 px-10 rounded-full shadow-2xl hover:bg-secondary hover:text-primary transition-all flex items-center gap-3 mx-auto">
                    <Download size={24} /> Download Application Form (PDF)
                </button>
                <p className="mt-4 text-gray-500">Or visit our campus to collect the prospectus kit.</p>
            </div>

        </div>
    );
};
export default Admissions;
